import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Typography, Box, Paper, TextField, Switch, Tooltip, Button, CircularProgress,
    Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { WithContext as ReactTags } from 'react-tag-input';
import { useSmartAlert, useUpdateSmartAlert, useSmartAlertRuns } from '../hooks/useSmartAlerts';
import { useToggleSmartAlert } from '../hooks/useSmartAlerts';
import { useNotification } from '../context/NotificationContext';
import PortalPicker from './PortalPicker';

const KeyCodes = { comma: 188, enter: 13 };
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const SectionHeader = ({ title, tooltip }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 4 }}>
        <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem' }}
        >
            {title}
        </Typography>
        {tooltip && (
            <Tooltip title={tooltip} placement="right">
                <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
        )}
    </Box>
);

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('hr-HR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

export default function SmartAlertDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showError, showSuccess } = useNotification();
    const { data: alertData, isLoading } = useSmartAlert(id);
    const updateSmartAlert = useUpdateSmartAlert();
    const toggleSmartAlert = useToggleSmartAlert();
    const { data: runs = [] } = useSmartAlertRuns(id);

    const [name, setName] = useState('');
    const [focusDescription, setFocusDescription] = useState('');
    const [selectedPortals, setSelectedPortals] = useState([]);
    const [termTags, setTermTags] = useState([]);
    const [excludingTermTags, setExcludingTermTags] = useState([]);
    const [emailTags, setEmailTags] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        document.title = alertData ? `${alertData.name} | Media Scanner` : 'Smart Alert | Media Scanner';
    }, [alertData]);

    useEffect(() => {
        if (!alertData || loaded) return;
        setName(alertData.name);
        setFocusDescription(alertData.focus_description || '');
        setSelectedPortals((alertData.portals || []).map(p => ({ id: p.id, name: p.name })));
        setTermTags((alertData.terms || []).map(t => ({ id: t.name, text: t.name })));
        setExcludingTermTags((alertData.excluding_terms || []).map(t => ({ id: t.name, text: t.name })));
        setEmailTags((alertData.email_addresses || []).map(e => ({ id: e, text: e })));
        setLoaded(true);
    }, [alertData, loaded]);

    const hasChanges = alertData && loaded && (
        name !== alertData.name ||
        (focusDescription || '') !== (alertData.focus_description || '') ||
        JSON.stringify(termTags.map(t => t.text).sort()) !== JSON.stringify((alertData.terms || []).map(t => t.name).sort()) ||
        JSON.stringify(excludingTermTags.map(t => t.text).sort()) !== JSON.stringify((alertData.excluding_terms || []).map(t => t.name).sort()) ||
        JSON.stringify(selectedPortals.map(p => p.id).sort()) !== JSON.stringify((alertData.portals || []).map(p => p.id).sort()) ||
        JSON.stringify(emailTags.map(t => t.text).sort()) !== JSON.stringify((alertData.email_addresses || []).sort())
    );

    const isFormValid = name.trim() && termTags.length > 0 && selectedPortals.length > 0 && emailTags.length > 0;

    const handleSave = () => {
        setSaving(true);
        updateSmartAlert.mutate(
            {
                id: parseInt(id),
                name: name.trim(),
                focus_description: focusDescription.trim() || null,
                list_portal_ids: selectedPortals.map(p => p.id),
                list_term_names: termTags.map(t => t.text),
                list_excluding_term_names: excludingTermTags.map(t => t.text),
                list_email_addresses: emailTags.map(t => t.text),
            },
            {
                onSuccess: () => {
                    showSuccess('Smart alert uspješno spremljen');
                    setSaving(false);
                    setLoaded(false);
                },
                onError: () => { showError('Greška pri spremanju'); setSaving(false); },
            }
        );
    };

    const handleToggle = () => {
        toggleSmartAlert.mutate(
            { id: parseInt(id), is_active: !alertData.is_active },
            {
                onSuccess: () => {
                    showSuccess(alertData.is_active ? 'Smart alert deaktiviran' : 'Smart alert aktiviran');
                    setLoaded(false);
                },
                onError: () => showError('Greška pri promjeni statusa'),
            }
        );
    };

    if (isLoading || !alertData) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
            <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                        {alertData.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 600, fontSize: '0.8rem',
                                backgroundColor: alertData.is_active ? '#e8f5e9' : '#f5f5f5',
                                color: alertData.is_active ? '#2e7d32' : '#999',
                            }}
                        >
                            {alertData.is_active ? 'Aktivan' : 'Neaktivan'}
                        </Typography>
                        <Switch size="small" checked={alertData.is_active} onChange={handleToggle} />
                    </Box>
                </Box>

                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>Naziv</Typography>
                    <TextField
                        fullWidth value={name} onChange={(e) => setName(e.target.value)}
                        size="small" sx={{ mb: 2 }}
                    />

                    <SectionHeader
                        title="Opis konteksta"
                        tooltip="Opišite kontekst pojma kako bi AI bolje razlikovao relevantne članke"
                    />
                    <TextField
                        fullWidth multiline rows={3}
                        placeholder="Npr. 'Pratim Krku kao farmaceutsku tvrtku, ne otok Krk'"
                        value={focusDescription} onChange={(e) => setFocusDescription(e.target.value)}
                        size="small"
                    />

                    <SectionHeader title="Pojmovi za pretragu" />
                    <ReactTags
                        tags={termTags} suggestions={[]} delimiters={delimiters}
                        handleDelete={(i) => setTermTags(termTags.filter((_, idx) => idx !== i))}
                        handleAddition={(tag) => setTermTags([...termTags, tag])}
                        inputFieldPosition="bottom"
                        placeholder="Unesi pojam, zatim zarez ili Enter"
                        autocomplete editable
                    />

                    <SectionHeader title="Isključujući pojmovi" tooltip="Članci koji sadrže ove pojmove neće biti uključeni" />
                    <ReactTags
                        tags={excludingTermTags} suggestions={[]} delimiters={delimiters}
                        handleDelete={(i) => setExcludingTermTags(excludingTermTags.filter((_, idx) => idx !== i))}
                        handleAddition={(tag) => setExcludingTermTags([...excludingTermTags, tag])}
                        inputFieldPosition="bottom"
                        placeholder="Unesi pojam za isključivanje, zatim zarez ili Enter"
                        autocomplete editable
                    />

                    <SectionHeader title="Portali" />
                    <PortalPicker value={selectedPortals} onChange={setSelectedPortals} showChips={false} autoDetectForeign />

                    <SectionHeader title="Email adrese za dostavu" />
                    <ReactTags
                        tags={emailTags} suggestions={[]} delimiters={delimiters}
                        handleDelete={(i) => setEmailTags(emailTags.filter((_, idx) => idx !== i))}
                        handleAddition={(tag) => setEmailTags([...emailTags, tag])}
                        inputFieldPosition="bottom"
                        placeholder="Unesi email adresu, zatim zarez ili Enter"
                        autocomplete editable
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
                        <Button variant="outlined" onClick={() => navigate('/smartAlerts')}>
                            Natrag na listu
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!hasChanges || !isFormValid || saving}
                            startIcon={saving ? <CircularProgress size={16} /> : null}
                        >
                            {saving ? 'Spremanje...' : 'Spremi promjene'}
                        </Button>
                    </Box>
                </Paper>

                {runs.length > 0 && (
                    <Paper sx={{ mt: 3, p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 2, fontSize: '1rem' }}>
                            Povijest izvršavanja
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Vrijeme</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Pronađeno</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Poslano</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Filtrirano</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {runs.map((run) => (
                                    <TableRow
                                        key={run.id} hover
                                        onClick={() => navigate(`/smart-alert-run/${run.id}`)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                            {formatDate(run.timestamp_started)}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                                                    backgroundColor: run.status === 'completed' ? '#e8f5e9' : run.status === 'failed' ? '#ffebee' : '#f5f5f5',
                                                    color: run.status === 'completed' ? '#2e7d32' : run.status === 'failed' ? '#c62828' : '#999',
                                                }}
                                            >
                                                {run.status === 'completed' ? 'Završeno' : run.status === 'failed' ? 'Greška' : run.status}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: 13 }}>{run.articles_matched ?? '—'}</TableCell>
                                        <TableCell align="center" sx={{ fontSize: 13 }}>{run.articles_sent ?? '—'}</TableCell>
                                        <TableCell align="center" sx={{ fontSize: 13 }}>{run.articles_filtered ?? '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Box>
        </Box>
    );
}
